import { useCallback, useEffect, useMemo, useState } from 'react';
import { Edge, Node } from '@xyflow/react';

type UseWorkflowDirtyStateProps = {
  draftNodes: Node[];
  draftEdges: Edge[];
  initialNodes: Node[];
  initialEdges: Edge[];
  onClose: () => void;
};

export function useWorkflowDirtyState({
  draftNodes,
  draftEdges,
  initialNodes,
  initialEdges,
  onClose,
}: UseWorkflowDirtyStateProps) {
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);

  // Check if there are unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    // Compare nodes and edges - deep comparison
    const nodesChanged = JSON.stringify(draftNodes) !== JSON.stringify(initialNodes);
    const edgesChanged = JSON.stringify(draftEdges) !== JSON.stringify(initialEdges);
    return nodesChanged || edgesChanged;
  }, [draftNodes, draftEdges, initialNodes, initialEdges]);

  // Handle browser close/reload warning
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = ''; // Modern browsers require this
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Handle close with unsaved changes check
  const handleClose = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowUnsavedChangesModal(true);
    } else {
      onClose();
    }
  }, [hasUnsavedChanges, onClose]);

  const handleConfirmClose = useCallback(() => {
    setShowUnsavedChangesModal(false);
    onClose();
  }, [onClose]);

  const handleCancelClose = useCallback(() => {
    setShowUnsavedChangesModal(false);
  }, []);

  return {
    hasUnsavedChanges,
    showUnsavedChangesModal,
    handleClose,
    handleConfirmClose,
    handleCancelClose,
  };
}
